import type { StoreId } from "@KassaroAB/shared/types/ids";
import {
  HelpChatStreamEventScheme,
  type HelpChatMessage,
  type HelpChatStreamEvent,
  type RestHelpPage,
} from "@KassaroAB/shared/types/help";
import { auth } from "../config/firebase";
import { API_BASE_URL, STREAM_API_BASE_URL } from "../constants/api";
import { api, ApiError } from "../utils/api";

const helpSessionRequest = async (
  method: "POST" | "DELETE",
  token: string | null,
): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/help/session`, {
    method,
    credentials: "include",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) {
    throw new ApiError(response.status, { message: response.statusText });
  }
};

export const createHelpSessionApi = async (): Promise<void> => {
  if (!auth.currentUser) {
    throw new Error("User not authenticated");
  }
  return helpSessionRequest("POST", await auth.currentUser.getIdToken());
};

export const deleteHelpSessionApi = (): Promise<void> =>
  helpSessionRequest("DELETE", null);

export const getHelpContextApi = (
  route: string,
  storeId: StoreId | null,
): Promise<{ pages: RestHelpPage[] }> => {
  const search = new URLSearchParams({ route });
  if (storeId) {
    search.set("storeId", storeId);
  }
  return api.get(`help/context?${search.toString()}`);
};

export async function* streamHelpChatApi(
  messages: HelpChatMessage[],
  storeId: StoreId | null,
  signal?: AbortSignal,
): AsyncGenerator<HelpChatStreamEvent> {
  if (!auth.currentUser) {
    throw new Error("User not authenticated");
  }
  const token = await auth.currentUser.getIdToken();
  const response = await fetch(`${STREAM_API_BASE_URL}/help/chat`, {
    method: "POST",
    headers: {
      Accept: "application/x-ndjson",
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ messages, ...(storeId ? { storeId } : {}) }),
    signal,
  });
  if (!response.ok) {
    throw new ApiError(response.status, { message: response.statusText });
  }
  const reader = response.body?.getReader();
  if (reader === undefined) {
    throw new Error("No response body");
  }
  const decoder = new TextDecoder();
  let buffer = "";
  while (true) {
    const { done, value } = await reader.read();
    if (done) {
      break;
    }
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop() ?? "";
    for (const line of lines.filter((item) => item.trim().length > 0)) {
      yield HelpChatStreamEventScheme.parse(JSON.parse(line));
    }
  }
  buffer += decoder.decode();
  if (buffer.trim().length > 0) {
    yield HelpChatStreamEventScheme.parse(JSON.parse(buffer));
  }
}
